import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from 'src/environments/environment';

export type AnalyticsProps = Record<string, string | number | boolean | undefined>;

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[] };

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    clarity?: ClarityFn;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private gtagDisabled = false;
  private clarityDisabled = false;
  private impersonating = false;
  private initialized = false;
  private lastClarityUserId?: string;

  constructor(private router: Router) { }

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    const measurementId = this.measurementId();
    if (measurementId) {
      this.loadGtag(measurementId);
    } else {
      this.gtagDisabled = true;
    }

    const clarityId = this.clarityProjectId();
    if (clarityId) {
      this.loadClarity(clarityId);
    } else {
      this.clarityDisabled = true;
    }

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => {
      const template = this.routeTemplate();
      this.page(template, this.safeDocumentTitle());
      this.captureRouteDerivedEvents(template);
    });
  }

  page(routeTemplate: string, title?: string): void {
    if (this.impersonating) {
      return;
    }
    const pageView: AnalyticsProps = {
      page_path: routeTemplate,
      page_title: title || routeTemplate
    };
    const pageLocation = this.sanitizedPageLocation(routeTemplate);
    if (pageLocation) {
      pageView.page_location = pageLocation;
    }
    this.callGtag('event', 'page_view', pageView);
    this.clarityIdentify(undefined, undefined, routeTemplate);
  }

  identify(userId: string, props?: AnalyticsProps): void {
    if (this.impersonating || !userId) {
      return;
    }
    this.callGtag('set', { user_id: String(userId) });
    const userProperties = this.sanitizeProps(props);
    if (userProperties) {
      this.callGtag('set', 'user_properties', userProperties);
    }
    this.clarityIdentify(String(userId));
  }

  reset(): void {
    this.callGtag('set', { user_id: undefined });
    this.clarityReset();
  }

  capture(name: string, props?: AnalyticsProps): void {
    if (this.impersonating || !name) {
      return;
    }
    this.callGtag('event', name, this.sanitizeProps(props) ?? {});
    this.clarityEvent(name);
  }

  setImpersonating(value: boolean): void {
    this.impersonating = value;
    if (value) {
      this.reset();
    }
  }

  private measurementId(): string {
    return (environment as { googleAnalyticsTrackingId?: string }).googleAnalyticsTrackingId?.trim() ?? '';
  }

  private clarityProjectId(): string {
    return (environment as { clarityProjectId?: string }).clarityProjectId?.trim() ?? '';
  }

  private loadClarity(projectId: string): void {
    try {
      if (typeof window.clarity !== 'function') {
        const clarity = function() {
          (clarity.q = clarity.q || []).push(arguments);
        } as ClarityFn;
        window.clarity = clarity;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
      script.onerror = () => {
        this.clarityDisabled = true;
      };
      document.head.appendChild(script);
    } catch (err) {
      this.clarityDisabled = true;
      console.debug('Analytics clarity load failed', err);
    }
  }

  private callClarity(...args: unknown[]): void {
    if (this.clarityDisabled) {
      return;
    }
    try {
      if (typeof window.clarity !== 'function') {
        return;
      }
      window.clarity(...args);
    } catch (err) {
      console.debug('Analytics clarity call failed', err);
    }
  }

  private loadGtag(measurementId: string): void {
    try {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false,
        debug_mode: !environment.production
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      script.onerror = () => {
        this.gtagDisabled = true;
      };
      document.head.appendChild(script);
    } catch (err) {
      this.gtagDisabled = true;
      console.debug('Analytics gtag load failed', err);
    }
  }

  private callGtag(...args: unknown[]): void {
    if (this.gtagDisabled) {
      return;
    }
    try {
      if (typeof window.gtag !== 'function') {
        return;
      }
      window.gtag(...args);
    } catch (err) {
      console.debug('Analytics gtag call failed', err);
    }
  }

  private sanitizeProps(props?: AnalyticsProps): Record<string, string | number | boolean> | undefined {
    if (!props) {
      return undefined;
    }
    const cleaned: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(props)) {
      if (value !== undefined && value !== null && value !== '') {
        cleaned[key] = value;
      }
    }
    return Object.keys(cleaned).length ? cleaned : undefined;
  }

  private routeTemplate(): string {
    const parts: string[] = [];
    let node = this.router.routerState.snapshot.root;
    while (node) {
      const path = node.routeConfig?.path;
      if (path) {
        parts.push(path);
      }
      node = node.firstChild;
    }
    return '/' + parts.join('/').replace(/\/+/g, '/');
  }

  private safeDocumentTitle(): string {
    try {
      return document.title || '';
    } catch {
      return '';
    }
  }

  private sanitizedPageLocation(routeTemplate: string): string | undefined {
    try {
      const origin = window.location.origin;
      if (!origin || origin === 'null') {
        return undefined;
      }
      const path = routeTemplate.startsWith('/') ? routeTemplate : `/${routeTemplate}`;
      return `${origin}${path}`;
    } catch {
      return undefined;
    }
  }

  private captureRouteDerivedEvents(template: string): void {
    this.captureDrilldownOpened(template);
    this.captureMapViewed(template);
    this.captureDiagnosticsOpened(template);
  }

  private captureDrilldownOpened(template: string): void {
    const match = template.match(/^\/customer\/dashboard\/(.+)$/);
    if (!match) {
      return;
    }
    const rest = match[1];
    const segment = rest.split('/')[0];
    if (!segment.includes('drilldown')) {
      return;
    }

    let widget: string | undefined;
    let level = 'summary';

    if (segment.startsWith('impact')) {
      widget = 'impact';
      if (segment.includes('machine')) {
        level = 'machine';
      } else if (segment.includes('operator')) {
        level = 'operator';
      } else if (segment.includes('no-driver')) {
        level = 'no_driver';
      }
    } else if (segment.startsWith('speeding')) {
      widget = 'speeding';
      if (segment.includes('machine')) {
        level = 'machine';
      } else if (segment.includes('operator')) {
        level = 'operator';
      } else if (segment.includes('no-operator')) {
        level = 'no_operator';
      }
    } else if (segment.startsWith('harsh')) {
      widget = 'harsh';
      if (segment.includes('machine')) {
        level = 'machine';
      } else if (segment.includes('operator')) {
        level = 'operator';
      } else if (segment.includes('no-operator')) {
        level = 'no_operator';
      }
    } else if (segment.startsWith('low-battery')) {
      widget = 'battery';
      level = segment.includes('detail') ? 'detail' : 'summary';
    } else if (segment.startsWith('checklist')) {
      widget = 'checklist';
      level = rest.includes('/') ? 'detail' : 'summary';
    } else if (segment.startsWith('utilisation')) {
      widget = 'utilisation';
    } else if (segment.startsWith('contract')) {
      widget = 'contract';
    } else if (segment.startsWith('service')) {
      widget = 'service';
    }

    if (!widget) {
      return;
    }
    this.capture('drilldown_opened', { area: 'customer', widget, level });
  }

  private captureMapViewed(template: string): void {
    if (/^\/customer\/fmx-google-map(\/|$)/.test(template)) {
      this.capture('map_viewed', { area: 'customer', map_type: 'google' });
      return;
    }
    if (/^\/customer\/fmx-map(\/|$)/.test(template)) {
      this.capture('map_viewed', { area: 'customer', map_type: 'leaflet' });
    }
  }

  private captureDiagnosticsOpened(template: string): void {
    if (/^\/admin\/fmx2-diagnostics(\/|$)/.test(template)) {
      this.capture('fmx2_diagnostics_opened', { area: 'admin' });
    }
  }

  private clarityIdentify(userId?: string, sessionId?: string, pageId?: string): void {
    if (userId) {
      this.lastClarityUserId = userId;
    }
    this.callClarity('identify', this.lastClarityUserId, sessionId, pageId);
  }

  private clarityEvent(name: string): void {
    this.callClarity('event', name);
  }

  private clarityReset(): void {
    try {
      this.lastClarityUserId = crypto.randomUUID();
    } catch {
      this.lastClarityUserId = `anon-${Date.now()}`;
    }
    this.callClarity('identify', this.lastClarityUserId);
  }
}
